import React, { useEffect, useMemo, useRef, useState } from 'react';
import './FilterPanel.scss';
import { Text, Image, Link, LinkBase } from '@fluentui/react';
import RequestTypeFilter from './requestTypeFilter/RequestTypeFilter';
import { SortFilter } from './sortFilter/SortFilter';
import ListActiveIcon from 'app/static/icons/list-view-active-icon.svg';
import ListIcon from 'app/static/icons/list-view-icon.svg';
import DetailsActiveIcon from 'app/static/icons/detail-view-active-icon.svg';
import DetailsIcon from 'app/static/icons/detail-view-icon.svg';
import { Trans } from 'react-i18next';
import CustomizeColumns from './draggableMenu/CustomizeColumns';
import { StatusFilter } from './statusFilter/StatusFilter';
import { IColumn, IColumnLookUpProps, IDateRangeProps, IStateData } from 'app/models/common/post';
import CountryFilter from './countryFilter/CountryFilter';
import { AgingFilter } from './agingFilter/AgingFilter';
import ProductsFilter from './productsFilter/ProductsFilter';
import { IUserProps } from 'app/models/common/response';
import AssignedToFilter from './assignedToFilter/AssignedToFilter';
import { IOptionProps } from '../models';
import _ from 'lodash';
import { RelationshipFilter } from './relationshipFilter/RelationshipFilter';
import { keyPress, setQueryParam } from 'app/components/helpers';
import { useHistory } from 'react-router-dom';
import useStore, { IStore } from 'app/store';
import { UserPreferencesDto } from 'generated/user-backend';

export interface IToggleProps {
    isListView: boolean;
    requestTypeSelection: IOptionProps[];
    handleToggleView: Function;
    setRequestTypeSelection: Function;
    list: IColumn[];
    showMenu: boolean;
    columnsLookUpData: IColumnLookUpProps[];
    setList: Function;
    handleChecked: Function;
    handleShowMenu: Function;
    setColumnsLookUp: Function;
    statusSelection: IOptionProps[];
    setStatusSelection: Function;
    agingSelection: IOptionProps[];
    setAgingSelection: Function;
    productsSelection: IOptionProps[];
    countrySelection: IOptionProps[];
    setProductsSelection: Function;
    setCountrySelection: Function;
    assignedToSelection: IUserProps[];
    setAssignedToSelection: Function;
    relationshipSelection: IOptionProps[];
    setRelationshipSelection: Function;
    options: IUserProps[];
    setOptions: Function;
    userPreference?: UserPreferencesDto;
    dateRange?: IDateRangeProps;
    activeRange?: string;
    DCFSelection: IOptionProps[];
    setDCFSelection: Function;
    setPreferenceDataFlag: Function;
    stateData?: IStateData;
    setListBuilderFilters?: Function;
    setPreviousLocation?: Function;
    // Added optional props to hide filters when reusing component (e.g. SearchResults page)
    hideRequestType?: boolean;
    hideAssignedTo?: boolean;
    // New props for SearchResults reuse: hide view toggle & customize columns
    hideViewToggle?: boolean;
    hideCustomizeColumns?: boolean;
    // Align all inner filters to the right instead of spaced
    alignFiltersRight?: boolean;
    // Optional sort filter shown at the start of the filter row
    showSort?: boolean;
    sortSelectedKey?: string;
    onSortChange?: (key: string) => void;
}
const FilterPanel: React.FC<IToggleProps> = ({
    isListView,
    handleToggleView,
    requestTypeSelection,
    setRequestTypeSelection,
    list,
    showMenu,
    columnsLookUpData,
    setList,
    handleChecked,
    handleShowMenu,
    setColumnsLookUp,
    statusSelection,
    setStatusSelection,
    agingSelection,
    setAgingSelection,
    productsSelection,
    countrySelection,
    setProductsSelection,
    setCountrySelection,
    assignedToSelection,
    setAssignedToSelection,
    relationshipSelection,
    setRelationshipSelection,
    options,
    userPreference,
    dateRange,
    activeRange,
    DCFSelection,
    setDCFSelection,
    setPreferenceDataFlag,
    stateData,
    setListBuilderFilters,
    setPreviousLocation,
    hideRequestType,
    hideAssignedTo,
    hideViewToggle,
    hideCustomizeColumns,
    alignFiltersRight,
    sortSelectedKey,
    onSortChange,
    showSort,
}) => {
    const filterPanelDisabled = useStore((state: IStore) => state.filterPanelDisabled);

    const [assignedToActive, setAssignedToActive] = useState<boolean>(false);
    const [requestTypeActive, setRequestTypeActive] = useState<boolean>(false);
    const [statusActive, setStatusActive] = useState<boolean>(false);
    const [agingActive, setAgingActive] = useState<boolean>(false);
    const [productsActive, setProductsActive] = useState<boolean>(false);
    const [countryActive, setCountryActive] = useState<boolean>(false);
    const [relationshipActive, setRelationshipActive] = useState<boolean>(false);

    const [activeFilters, setActiveFilters] = useState<number>(0);

    const agingOptions = useMemo(() => {
        const options: IOptionProps[] = [
            { key: 'all', text: 'All' },
            {
                key: '0d-7d',
                text: '0d - 7d'
            },
            {
                key: '8d-30d',
                text: '8d - 30d'
            },
            {
                key: '31d-plus',
                text: '31d+'
            }
        ];
        return options;
    }, [dateRange, activeRange]);

    // Commenting out because it is currently unused. However, we may want to have this available later, so not deleting yet
    // const showDCFfilter = () => {
    //     if (requestTypeSelection?.length === 0) return true;
    //     if (
    //         requestTypeSelection?.filter(
    //             (item) => item.key === 'pre_sales_request' || item.key === 'post_sales_request' || item.key === 'warranty_request'
    //         ).length > 0
    //     )
    //         return true;
    //     return false;
    // };

    const history = useHistory();

    const clearFilter = () => {
        setDCFSelection([]);
        setRequestTypeSelection([]);
        setAgingSelection([]);
        setStatusSelection([]);
        setAssignedToSelection([]);
        setProductsSelection([]);
        setCountrySelection([]);
        setRelationshipSelection([]);
        setListBuilderFilters(null);
        setPreviousLocation('');
        const dataObj: IStateData = {
            DCFSelection: [],
            requestTypeSelection: [],
            agingSelection: [],
            statusSelection: [],
            assignedToSelection: [],
            productsSelection: [],
            countrySelection: [],
            relationshipSelection: []
        };
        setActiveFilters(0);
    const targetPath = history.location.pathname;
    // Always reset to page 1 when filters are cleared
        setQueryParam(history, targetPath, { page: 1 }, false, {
            ...stateData,
            ...dataObj
        });
    };

    interface ISelectionsState {
        requestTypeSelection: IOptionProps[];
        statusSelection: IOptionProps[];
        agingSelection: IOptionProps[];
        productsSelection: IOptionProps[];
        countrySelection: IOptionProps[];
        assignedToSelection: IUserProps[];
        relationshipSelection: IOptionProps[];
        DCFSelection: IOptionProps[];
    }

    // Consolidated state snapshot for building query params
    const currentSelectionsState: ISelectionsState = useMemo(() => ({
        requestTypeSelection,
        statusSelection,
        agingSelection,
        productsSelection,
        countrySelection,
        assignedToSelection,
        relationshipSelection,
        DCFSelection
    }), [requestTypeSelection, statusSelection, agingSelection, productsSelection, countrySelection, assignedToSelection, relationshipSelection, DCFSelection]);

    // Track previous selection snapshot to avoid redundant URL updates
    const prevSelectionsRef = useRef<ISelectionsState | null>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        if (filterPanelDisabled) return;
        const hasAnySelection = Object.values(currentSelectionsState).some((arr: ISelectionsState) => Array.isArray(arr) && arr.length > 0);
        // If no selections, don't push (preserves existing cleared URL without loops)
        if (!hasAnySelection) return;
        // Shallow compare arrays by key length + keys
        const changed = (() => {
            if (!prevSelectionsRef.current) return true;
            const prev = prevSelectionsRef.current;
            for (const key of Object.keys(currentSelectionsState) as (keyof typeof currentSelectionsState)[]) {
                const curArr = currentSelectionsState[key] as IOptionProps[] | IUserProps[];
                const prevArr = (prev[key] as IOptionProps[] | IUserProps[]) || [];
                if (curArr.length !== prevArr.length) return true;
                // Compare by key/id property
                for (let i = 0; i < curArr.length; i++) {
                    const cKey = (curArr[i] as IOptionProps)?.key ?? (curArr[i] as IUserProps)?.id;
                    const pKey = (prevArr[i] as IOptionProps)?.key ?? (prevArr[i] as IUserProps)?.id;
                    if (cKey !== pKey) return true;
                }
            }
            return false;
        })();
        if (!changed) return;
        // Debounce rapid changes (multi-select cascades)
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            const targetPath = history.location.pathname;
            const dataObj = { ...stateData, ...currentSelectionsState };
            setQueryParam(history, targetPath, { page: 1 }, false, dataObj);
            prevSelectionsRef.current = currentSelectionsState;
        }, 150); // 150ms debounce
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [currentSelectionsState, filterPanelDisabled, stateData, history]);
    useEffect(() => {
        if (userPreference) {
            let localFlag = {
                aging: false,
                assignee: false,
                caseStatus: false,
                requestType: false,
                deviceModel: false,
                countryModel: false,
                dcfRequested: false,
                relationship: false
            };

            try {
                const agingParsed = JSON.parse(userPreference.aging);
                if (agingParsed) {
                    let agingPreferenceValue = [];
                    for (const item of agingParsed) {
                        const matchedItem = _.find(agingOptions, (ele) => ele.key === item);
                        agingPreferenceValue.push(matchedItem);
                    }

                    setAgingSelection(agingPreferenceValue);
                    localFlag = { ...localFlag, aging: true };
                } else {
                    setAgingSelection([]);
                    localFlag = { ...localFlag, aging: true };
                }
            } catch (error) {
                setAgingSelection([]);
                localFlag = { ...localFlag, aging: true };
            }
            try {
                const assigneeParsed = userPreference.assignee ? JSON.parse(userPreference.assignee) : [];
                setAssignedToSelection(assigneeParsed);
                localFlag = { ...localFlag, assignee: true };
            } catch (error) {
                setAssignedToSelection([]);
                localFlag = { ...localFlag, assignee: true };
            }
            try {
                const caseStatusParsed = userPreference.caseStatus ? JSON.parse(userPreference.caseStatus) : [];
                setStatusSelection(caseStatusParsed);
                localFlag = { ...localFlag, caseStatus: true };
            } catch (error) {
                setStatusSelection([]);
                localFlag = { ...localFlag, caseStatus: true };
            }
            try {
                const requestTypeParsed = userPreference.requestType ? JSON.parse(userPreference.requestType) : [];
                setRequestTypeSelection(requestTypeParsed);
                localFlag = { ...localFlag, requestType: true };
            } catch (error) {
                setRequestTypeSelection([]);
                localFlag = { ...localFlag, requestType: true };
            }
            try {
                const deviceModelParsed = userPreference.deviceModel ? JSON.parse(userPreference.deviceModel) : [];
                setProductsSelection(deviceModelParsed);
                localFlag = { ...localFlag, deviceModel: true };
            } catch (error) {
                setProductsSelection([]);
                localFlag = { ...localFlag, deviceModel: true };
            }
            // UseGetUserPreferences does not currently return a country model. If we update this in the future we can add it here
            // try {
            //     const countryModelParsed = userPreference.countryModel ? JSON.parse(userPreference.countryModel) : [];
            //     setCountrySelection(countryModelParsed);
            //     localFlag = { ...localFlag, countryModel: true };
            // } catch (error) {
                setCountrySelection([]);
                localFlag = { ...localFlag, countryModel: true };
            // }
            try {
                const dcfRequestedParsed = userPreference.dcfRequest ? JSON.parse(userPreference.dcfRequest) : [];
                setDCFSelection(dcfRequestedParsed);
                localFlag = { ...localFlag, dcfRequested: true };
            } catch (error) {
                setDCFSelection([]);
                localFlag = { ...localFlag, dcfRequested: true };
            }
            try {
                const relationshipParsed = userPreference.relationship ? JSON.parse(userPreference.relationship) : [];
                setRelationshipSelection(relationshipParsed);
                localFlag = { ...localFlag, relationship: true };
            } catch (error) {
                setRelationshipSelection([]);
                localFlag = { ...localFlag, relationship: true };
            }
            let count = 0;
            Object.entries(localFlag).forEach(([key, val]) => {
                if (val) {
                    count += 1;
                }
            });

            if (count === 8) {
                setPreferenceDataFlag(true);
            }
        }
    }, [userPreference, agingOptions]);

    // Only clear selections when panel transitions from enabled -> disabled (not on every render while disabled)
    const wasDisabledRef = useRef<boolean>(filterPanelDisabled);
    useEffect(() => {
        if (filterPanelDisabled && !wasDisabledRef.current) {
            setDCFSelection([]);
            setRequestTypeSelection([]);
            setAgingSelection([]);
            setStatusSelection([]);
            setAssignedToSelection([]);
            setProductsSelection([]);
            setCountrySelection([]);
            setRelationshipSelection([]);
            setActiveFilters(0);
        }
        wasDisabledRef.current = filterPanelDisabled;
    }, [filterPanelDisabled]);

    useEffect(() => {
        let activeCount = 0;

        if (!hideAssignedTo && assignedToActive) {
            activeCount++;
        }

        if (!hideRequestType && requestTypeActive) {
            activeCount++;
        }

        if (statusActive) {
            activeCount++;
        }

        if (agingActive) {
            activeCount++;
        }

        if (productsActive) {
            activeCount++
        }

        if (countryActive) {
            activeCount++;
        }

        if (relationshipActive) {
            activeCount++
        }

        setActiveFilters(activeCount);
    }, [assignedToActive, requestTypeActive, statusActive, agingActive, productsActive, countryActive, relationshipActive]);

    return (
        <>
            <div className={`list-view-toggle row-flex-box justify-sb ${hideViewToggle ? 'no-toggle' : ''}`}>
                {!hideViewToggle && (
                    <div className="row-flex-box align-center">
                        <Text className={`view-text ${isListView ? 'active' : ''}`}>
                            <Trans>List View</Trans>
                        </Text>
                        <div className="lv-toggle-wrap">
                            <div
                                className={`lv-toggle ${isListView ? 'handle-left' : ''}`}
                                tabIndex={0}
                                onClick={() => handleToggleView(true)}
                                onKeyPress={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                                        handleToggleView(true);
                                    }
                                }}
                                data-testid="toggle-button1">
                                {isListView && <Image src={ListActiveIcon} alt="Acitve" />}
                                {!isListView && <Image src={ListIcon} alt="Inacitve" />}
                            </div>
                            <div
                                className={`lv-toggle ${!isListView ? 'handle-right' : ''}`}
                                tabIndex={0}
                                onClick={() => handleToggleView(false)}
                                onKeyPress={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                                        handleToggleView(false);
                                    }
                                }}
                                data-testid="toggle-button2">
                                {!isListView && <Image src={DetailsActiveIcon} alt="Acitve" />}
                                {isListView && <Image src={DetailsIcon} alt="Inacitve" />}
                            </div>
                        </div>
                        <Text className={`view-text ${!isListView ? 'active' : ''}`}>
                            <Trans>Detail View</Trans>
                        </Text>
                    </div>
                )}
                <div className={`filter-panel row-flex-box align-center ${hideViewToggle ? 'filter-panel-no-toggle' : ''} ${alignFiltersRight ? 'filters-right' : ''}`}>
                    {showSort && sortSelectedKey !== undefined && onSortChange && (
                        <SortFilter selectedKey={sortSelectedKey} onChange={onSortChange} />
                    )}
                    {/* {showDCFfilter() && (
                        <DCFfilter selectedKeys={DCFSelection} setSelectedKeys={setDCFSelection} stateData={stateData} />
                    )} */}
                    {!hideRequestType && (
                        <RequestTypeFilter
                            selectedKeys={requestTypeSelection}
                            setSelectedKeys={setRequestTypeSelection}
                            origin={'ADMIN'}
                            stateData={stateData}
                            setFilterActive={setRequestTypeActive}
                        />
                    )}
                    {!hideAssignedTo && (
                        <AssignedToFilter
                            selectedKeys={assignedToSelection}
                            setSelectedKeys={setAssignedToSelection}
                            origin="FILTER_PANEL"
                            options={options}
                            stateData={stateData}
                            setFilterActive={setAssignedToActive}
                        />
                    )}
                    <StatusFilter 
                        selectedKeys={statusSelection} 
                        setSelectedKeys={setStatusSelection} 
                        stateData={stateData} 
                        setFilterActive={setStatusActive}
                    />
                    <AgingFilter
                        selectedKeys={agingSelection}
                        setSelectedKeys={setAgingSelection}
                        options={agingOptions}
                        stateData={stateData}
                        setFilterActive={setAgingActive}
                    />
                    <ProductsFilter
                        isListView={isListView}
                        selectedKeys={productsSelection}
                        setSelectedKeys={setProductsSelection}
                        stateData={stateData}
                        setFilterActive={setProductsActive}
                    />
                    <CountryFilter
                        isListView={isListView}
                        selectedKeys={countrySelection}
                        setSelectedKeys={setCountrySelection}
                        stateData={stateData}
                        setFilterActive={setCountryActive}
                    />
                    <RelationshipFilter
                        selectedKeys={relationshipSelection}
                        setSelectedKeys={setRelationshipSelection}
                        stateData={stateData}
                        setFilterActive={setRelationshipActive}
                    />
                    {isListView && !hideCustomizeColumns && (
                        <CustomizeColumns
                            list={list}
                            setList={setList}
                            handleChecked={handleChecked}
                            showMenu={showMenu}
                            handleShowMenu={handleShowMenu}
                            columnsLookUpData={columnsLookUpData}
                            setColumnsLookUp={setColumnsLookUp}
                        />
                    )}
                    <div className="clear-btn-container">
                        <Link
                            data-testid="clear-btn-test"
                            className={`clear-filter-btn ${filterPanelDisabled ? 'filter-disabled' : ''}`}
                            tabIndex={0}
                            onKeyPress={(e: React.KeyboardEvent<HTMLAnchorElement | HTMLElement | HTMLButtonElement | LinkBase>) => keyPress(e, clearFilter)}
                            onClick={() => clearFilter()}
                            disabled={filterPanelDisabled}>
                            Clear filters
                        </Link>
                        {activeFilters > 0 && <Text className="filter-active-count" block>{activeFilters}</Text>}
                    </div>
                </div>
            </div>
        </>
    );
};

export default FilterPanel;
